import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { supabase } from "./supabase";
import { store, groupByMonth, buildCalendar, buildTrends, tagList, allTags, placeWeather, type JournalEntry, type WeekSignals, type FactTone, type CalendarMonth, type Trends } from "./store";
import { weeklyReflection, openWebLogin, BrainError, type Reflection } from "./brain";
import { promptForToday } from "./prompts";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./helpers/ios-native-kit";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import { PhotoViewer } from "./PhotoViewer";
import Chat from "./chat/Chat";
import { Lock, Sparkles, Trash2, Pencil, Check, X, BookOpen, WifiOff, RefreshCw, AlertCircle, Search, Download } from "lucide-react";
import { buildReadable, readableFilename, type ReadableFormat } from "./readable-export";
import { saveReadableCopy } from "./localFolder";
import { isTauri } from "./web-runtime";
import { loadReminder, saveReminder, syncReminder, type ReminderSetting } from "./reminders";
import moodMarkUrl from "./assets/mood-mark.svg";
import promptQuill from "./assets/prompt-quill.svg";
import onThisDayMark from "./assets/on-this-day.svg";
import reflectionMark from "./assets/reflection.svg";
import emptyNoEntries from "./assets/empty-no-entries.svg";

const MOOD_LABELS = ["Low", "Down", "Okay", "Good", "Great"];

// Nocturne — the 5-step moon-phase mood glyph (mood-mark.svg = 5 tiles). Mood is never
// color-alone: the phase FORM carries the value. Tile n = mood n (1 = mostly dark → 5 = full).
function MoodMark({ mood, size = 16, title }: { mood: number; size?: number; title?: string }) {
  const m = Math.min(5, Math.max(1, mood));
  return (
    <span title={title} aria-label={title} role={title ? "img" : undefined} style={{
      display: "inline-block", width: size, height: size, borderRadius: "50%", flexShrink: 0,
      backgroundImage: `url("${moodMarkUrl}")`, backgroundRepeat: "no-repeat",
      backgroundSize: `${size * 5}px ${size}px`, backgroundPosition: `${-(m - 1) * size}px 0`,
    }} />
  );
}
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

/**
 * The ribbon bookmark sewn into a journal's spine — a band with the swallowtail cut at its end.
 * Replaces the lucide `Sparkles` shared by 20 of the fleet's 26 chat launchers; the button's
 * outline changes with it, since the same glyph in a recoloured pill still reads as the same
 * control to a reviewer comparing two of our apps (Guideline 4.3(a)).
 */
function RibbonMark({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M7 3h10v18l-5-4.4L7 21z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // §2/§3 (#62, Apple Guideline 5.1.1i) — AI-data consent shown ONCE on first launch
  // (localStorage), before any AI call. Names the data + AI vendors + links Privacy.
  const [consented, setConsented] = useState(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, []);

  // Deep-link auth: web login returns solo-journal://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  if (!mounted && !SCREENSHOT) {
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center" }}>
        <div className="row" style={{ flexDirection: "column", gap: "0.6rem", color: "var(--ink-3)" }}>
          <BookOpen size={28} style={{ color: "var(--accent)", opacity: 0.8 }} />
          <span style={{ fontSize: "var(--fs-sm)" }}>Opening your journal…</span>
        </div>
      </div>
    );
  }
  if (!consented && !SCREENSHOT) {
    // 🔒 The LOCKED AI-consent notice — one notice, every app, only the app name varies.
    return <AiConsent brand="Oriora Journal"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!authed && !SCREENSHOT) return <Login />;
  return <Dashboard email={SCREENSHOT ? "you@oriora.app" : email} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ minHeight: "100%", display: "grid", placeItems: "center", padding: "max(1.5rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) max(1.5rem, env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 400, padding: "2rem", textAlign: "center" }}>
        <div className="row" style={{ justifyContent: "center", color: "var(--accent)", marginBottom: "0.4rem" }}><BookOpen size={26} /></div>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Oriora Journal</div>
        <h1 style={{ fontSize: "1.4rem", margin: "0.4rem 0 0.4rem" }}>Sign in with Oriora</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account to sign in, then returns here. Your writing lives on this
          device — AI features send only what each request needs, and Oriora doesn't retain it. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // §2 (#61, Apple Guideline 4) — iOS signs in via in-app ASWebAuthenticationSession;
            // desktop keeps the external-browser handoff (deep-link returns the session).
            try {
              const p = platform();
              if (p === "ios") await signInIos("solo-journal");
              else if (p === "macos") await signInMac("solo-journal");
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/solo-journal/";
              else await openWebLogin();
            }
            finally { setBusy(false); }
          }}>
          {busy ? "Opening sign-in…" : "Sign in with Oriora"}
        </button>
        {/* §2 (Apple Guideline 5.1.1v) — in-app account deletion is required even pre-sign-in. */}
        <button className="btn" style={{ width: "100%", marginTop: "0.6rem", fontSize: "var(--fs-xs)", color: "var(--ink-3)", background: "none", border: "none" }}
          onClick={openAccountDelete}>Delete account</button>
      </div>
    </main>
  );
}

function Dashboard({ email }: { email: string | null }) {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [editing, setEditing] = useState<JournalEntry | null>(null);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not change
  // any existing screen. onChanged → reload() refreshes the timeline after a chat-driven mutation.
  const [chatOpen, setChatOpen] = useState(false);
  // Offline banner — entries still persist to SQLite; only the AI reflection pauses.
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  const composeRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState("");
  const reload = useCallback(async () => { setEntries(await store.listEntries()); }, []);

  // Search runs on the device against title + body — the query never leaves it. Empty query
  // falls straight back to the full list, so clearing the box is instant.
  const [hits, setHits] = useState<JournalEntry[] | null>(null);
  useEffect(() => {
    const q = query.trim();
    if (!q) { setHits(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      store.searchEntries(q).then((r) => { if (!cancelled) setHits(r); }).catch(() => { if (!cancelled) setHits([]); });
    }, 120);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, entries]);
  const shown = hits ?? entries;

  useEffect(() => { void reload(); }, [reload]);

  // Track connectivity so the reflection card can pause its AI call honestly.
  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Native menu actions → focus compose / scroll.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      if (e.payload === "new") {
        composeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        composeRef.current?.querySelector<HTMLTextAreaElement>("textarea")?.focus();
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const grouped = useMemo(() => groupByMonth(shown), [shown]);
  // Two ways of reading the same book: as days you can read, or as a shape you can see.
  const [view, setView] = useState<"days" | "calendar" | "trends">("days");
  const calendar = useMemo(() => buildCalendar(shown), [shown]);
  const trends = useMemo(() => buildTrends(entries), [entries]);
  const tagCloud = useMemo(() => allTags(entries), [entries]);
  const openEntry = useCallback((id: string) => {
    const e = entries.find((x) => x.id === id);
    if (!e) return;
    setEditing(e);
    composeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [entries]);

  // G21 — a photo you can tap open, full screen, pinch/zoom/rotate. One viewer over ALL entry
  // photos (chronological) so ‹ › steps through them like an album.
  // Only data-URLs are displayable: pre-2026-07-10 desktop builds stored raw FILE PATHS in
  // photo_ref, which CSP (img-src 'self' data:) can never render — those show as no-photo
  // instead of a broken image. The entry text is untouched; re-adding the photo re-stores it.
  const photoEntries = useMemo(
    () => [...entries]
      .filter((e) => !!e.photo_ref && e.photo_ref.startsWith("data:"))
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    [entries],
  );
  const [viewerIdx, setViewerIdx] = useState<number | null>(null);
  const openPhoto = useCallback((id: string) => {
    setViewerIdx((prev) => {
      const idx = photoEntries.findIndex((e) => e.id === id);
      return idx >= 0 ? idx : prev;
    });
  }, [photoEntries]);

  return (
    <main style={{ maxWidth: 720, width: "100%", margin: "0 auto", padding: "max(2rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) max(4rem, env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))" }}>
      {offline && (
        <div role="status" className="banner-offline">
          <WifiOff size={14} style={{ flexShrink: 0 }} /> Offline — your writing still saves here; AI reflection resumes when you reconnect.
        </div>
      )}
      {/* R5 — header demoted: no count, no streak; one quiet privacy line. */}
      <header className="app-header">
        <div>
          <div className="app-header__title">
            <MoodMark mood={5} size={20} /> Journal
          </div>
          {/* R1 lives HERE, not only in the empty card below: at 390 the compose card fills the
              first viewport, so a line further down is not "the first screen". This slot already
              carried a claim — "Private to this device" — that Cloud Backup makes untrue as an
              absolute, so one line does both jobs: honest about where the writing lives, and it
              names the BETTER row (own-cloud backup · no subscription) chosen by the rule. */}
          <div className="app-header__meta row" style={{ gap: "0.3rem" }}><Lock size={11} /> Yours on this device unless you back it up — no subscription</div>
        </div>
        {/* Account: THE canonical Oriora account menu (avatar chip → sign out / delete / wallet). */}
        <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
          <BackupMenu
            appName="Journal" fileSlug="journal" appScheme="solo-journal"
            hasData={entries.length > 0} onRestored={reload}
          />
          <AccountMenu email={email} appSlug="solo-journal" appName="Oriora Journal" />
          <AddFundsMenu />
        </div>
      </header>

      {/* R1 — Today's Page leads the whole screen. */}
      <div ref={composeRef}>
        <Compose editing={editing} onDone={() => { setEditing(null); void reload(); }} onCancel={() => setEditing(null)} />
      </div>

      {/* R2 — the reflection is a companion below today's writing, never the doorman. */}
      <WeeklyReflection entries={entries} offline={offline} />

      <OnThisDay entries={entries} onOpenPhoto={openPhoto} />

      {entries.length === 0 ? (
        <div className="card muted" style={{ padding: "2.5rem 1.5rem", textAlign: "center", marginTop: "1.25rem" }}>
          <img src={emptyNoEntries} alt="" style={{ width: 170, height: 128, display: "block", margin: "0 auto" }} />
          <div style={{ fontWeight: 600, color: "var(--ink-1)", marginTop: "0.6rem" }}>Your journal starts here</div>
          {/* R1 (ready-state 2026-09-25). The BETTER row chosen by the rule: it must be in the
              matrix's `HAVE / BETTER — 9 rows` cell AND be something Day One's own pricing page
              shows Basic withholds. Row 1 lists "own-cloud backup · no subscription", and
              dayoneapp.com/pricing caps Basic at ONE DEVICE with Silver/Gold above it — so
              portability-without-a-subscription is the row. Deliberately NOT chosen: photos (their
              Basic allows 1 per entry and `db.rs:29 photo_ref` is a single column here too — not
              better), and Calendar/Map/Media + unlimited journals (free on their Basic).
              ⚠️ "restore", never "sync" — live cross-device sync is DECLINED in the matrix (row 11).
              Also fixes a §7b-class claim the gate's regex misses: "Everything is stored on this
              device" while Cloud Backup ships ("stored on" does not match `stays? on this device`). */}
          <div style={{ fontSize: "var(--fs-sm)", marginTop: 4, maxWidth: 360, marginInline: "auto", lineHeight: 1.5 }}>
            Answer today's prompt above — a fresh one each day — or just write whatever's on your mind.
            Your entries stay on this device unless you back them up, and a backup goes to your own
            cloud — so the journal moves to another device whenever you restore it, with nothing to
            subscribe to.
          </div>
        </div>
      ) : (
        <div className="book-of-days">
          <div className="book-head">
            <div className="section-head">The book of days</div>
            <input
              className="book-search"
              value={query}
              onChange={(ev) => setQuery(ev.target.value)}
              placeholder="Search what you've written…"
              aria-label="Search your entries"
            />
            {query && (
              <button className="btn" aria-label="Clear search" title="Clear search"
                style={{ border: "none", background: "none", padding: 0, color: "var(--ink-3)" }}
                onClick={() => setQuery("")}><X size={13} /></button>
            )}
            {(["days", "calendar", "trends"] as const).map((v) => (
              <button key={v} className="btn btn-ghost btn-sm"
                style={{ flexShrink: 0, color: view === v ? "var(--ink-1)" : "var(--ink-3)",
                         textDecoration: view === v ? "underline" : "none", textUnderlineOffset: 4 }}
                aria-pressed={view === v}
                onClick={() => setView(v)}>
                {v === "days" ? "Days" : v === "calendar" ? "Calendar" : "Trends"}
              </button>
            ))}
          </div>
          {tagCloud.length > 0 && view === "days" && (
            <div className="tag-row">
              {tagCloud.slice(0, 8).map(({ tag, count }) => (
                <button key={tag} className={`tag-pick${query.trim() === tag ? " tag-pick--on" : ""}`}
                  onClick={() => setQuery(query.trim() === tag ? "" : tag)}
                  title={`${count} ${count === 1 ? "entry" : "entries"}`}>{tag}</button>
              ))}
            </div>
          )}
          {query.trim() && (
            <p className="book-found">
              {shown.length === 0
                ? `Nothing you've written mentions “${query.trim()}”.`
                : `${shown.length} ${shown.length === 1 ? "day" : "days"} mention “${query.trim()}” — found on this device.`}
            </p>
          )}
          {view === "trends" ? (
            <TrendsView t={trends} />
          ) : view === "calendar" ? (
            <Calendar months={calendar} onOpen={openEntry} />
          ) : (
          grouped.map((g) => (
            <section key={g.label} className="book-of-days__month">
              <div className="section-head">{g.label}</div>
              <div className="card" style={{ overflow: "hidden" }}>
                {g.items.map((e) => (
                  <EntryRow key={e.id} e={e} onEdit={() => { setEditing(e); composeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }} onChange={reload} onOpenPhoto={() => openPhoto(e.id)} />
                ))}
              </div>
            </section>
          )))}

          {/* The occasional actions live here, under the book — not between the page you write on
              and the days you've written. Nocturne demoted everything that isn't the writing; these
              are set once and then forgotten, so they get a quiet footer. */}
          <div className="keeping">
            <div className="section-head" style={{ marginTop: 0 }}>Keeping</div>
            <div className="keeping__row">
              <ReadableExport entries={entries} />
              <DailyReminder />
            </div>
          </div>
        </div>
      )}

      {/* Chat panel — a natural-language way to use the journal by typing. Purely additive: the
          launcher + slide-over live on top; no existing screen is altered. A chat-driven change
          reloads the timeline. */}
      {!chatOpen && (
        <button className="jcp-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
          <RibbonMark size={18} />
        </button>
      )}
      <aside className={`jcp-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="jcp-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to add, find, or reflect</span>
          <button className="jcp-link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>

      {viewerIdx != null && photoEntries.length > 0 && (
        <PhotoViewer
          photos={photoEntries.map((e) => ({ src: e.photo_ref, alt: e.title || "Journal photo" }))}
          startIndex={Math.min(viewerIdx, photoEntries.length - 1)}
          onClose={() => setViewerIdx(null)}
        />
      )}
    </main>
  );
}

// "On this day" — the memory-keeper flashback (Day One's table-stake): entries written on this
// same calendar day in earlier months/years, straight from the local store. Computed on-device,
// free; hidden until there is something to remember.
/**
 * Save the journal as something you can read — Markdown or plain text.
 *
 * Deliberately separate from Backup: the JSON backup restores byte-for-byte and is unreadable;
 * this is the keepsake. Third-most-common complaint about the journals people pay for is not
 * being able to get their writing out in a form they can keep (23 of 140 negative reviews).
 * Built on the device — nothing is sent anywhere.
 */
/**
 * A daily nudge to write, at a time the customer picks.
 *
 * Fires from the OS on this device — no server, no push service; nothing about the reminder or the
 * journal leaves the phone. Hidden on the web, where there is no notification plugin: better to not
 * offer it than to offer a switch that silently does nothing.
 */
function DailyReminder() {
  const [setting, setSetting] = useState<ReminderSetting>(loadReminder);
  const [msg, setMsg] = useState<string | null>(null);

  // Re-lay the schedule on launch so the fortnight ahead always stays filled.
  useEffect(() => { if (setting.enabled) void syncReminder(setting); /* eslint-disable-next-line */ }, []);

  if (!isTauri()) return null; // no notification plugin in the browser — don't offer a dead switch

  const apply = async (next: ReminderSetting) => {
    setSetting(next);
    saveReminder(next);
    const on = await syncReminder(next);
    setMsg(
      !next.enabled ? "Reminder off."
      : on ? `We'll nudge you at ${next.time}, on this device.`
      : "Notifications are off for this app — turn them on in Settings to be reminded.",
    );
  };

  return (
    <div className="row" style={{ gap: "0.45rem", alignItems: "center", flexWrap: "wrap" }}>
      {/* The label styling lives in `.keeping label` (styles.css) so it reads as prose in the
          footer, not as a form control in the writing flow. */}
      <label>
        <input
          type="checkbox"
          checked={setting.enabled}
          onChange={(ev) => void apply({ ...setting, enabled: ev.target.checked })}
          aria-label="Remind me to write"
        />
        Remind me to write
      </label>
      {setting.enabled && (
        <input
          type="time"
          value={setting.time}
          aria-label="Reminder time"
          onChange={(ev) => void apply({ ...setting, time: ev.target.value })}
        />
      )}
      {msg && <span className="keeping__note" style={{ marginTop: 0 }}>{msg}</span>}
    </div>
  );
}

/**
 * The calendar of days — the category's signature surface (Day One's day-cell is that day's photo;
 * Daylio calls it "Year in Pixels"). Pure date maths over entries we already store: no AI, no
 * network, nothing sent.
 *
 * Nocturne's rule holds: mood is never colour-alone, so a written day carries its moon-phase mark
 * and an unwritten day is simply absent rather than a red square.
 */
function Calendar({ months, onOpen }: { months: CalendarMonth[]; onOpen: (id: string) => void }) {
  const todayKey = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  if (months.length === 0) return null;
  return (
    <div className="cal">
      {months.map((m) => (
        <section key={m.label} className="cal__month">
          <div className="cal__head">
            <div className="section-head" style={{ margin: 0 }}>{m.label}</div>
            <span className="cal__written">
              {m.written} {m.written === 1 ? "day" : "days"} written
            </span>
          </div>
          <div className="cal__dow" aria-hidden>
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <span key={i}>{d}</span>)}
          </div>
          <div className="cal__grid">
            {m.cells.map((c, i) => {
              if (!c) return <div key={`b${i}`} className="cal__cell cal__cell--blank" />;
              const written = !!c.entry;
              const photo = written && c.entry!.photo_ref ? c.entry!.photo_ref : "";
              const cls = [
                "cal__cell",
                written ? "cal__cell--written" : "cal__cell--empty",
                photo ? "cal__cell--photo" : "",
                c.date === todayKey ? "cal__cell--today" : "",
              ].filter(Boolean).join(" ");
              if (!written) return <div key={c.date} className={cls}><span className="cal__num">{c.day}</span></div>;
              const e = c.entry!;
              return (
                <button key={c.date} className={cls} onClick={() => onOpen(e.id)}
                  title={`${e.title || "Entry"} — ${fmtDate(e.created_at)}`}
                  aria-label={`${e.title || "Entry"}, ${fmtDate(e.created_at)}`}>
                  {photo && <img src={photo} alt="" />}
                  <span className="cal__num">{c.day}</span>
                  {!photo && <span className="cal__mark"><MoodMark mood={e.mood} size={9} /></span>}
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * Trends — your own patterns over time. Daylio's whole product; Stoic and Reflectly both sell it.
 * Computed on the device from entries we already store: no AI call, nothing sent.
 *
 * Descriptive, never advisory — it says what you did, not what it means or what to do about it.
 */
function TrendsView({ t }: { t: Trends }) {
  if (t.totalEntries === 0) return null;
  const peak = Math.max(1, ...t.months.map((m) => m.entries));
  const moodWord = ["", "low", "quiet", "steady", "good", "bright"][Math.round(t.meanMood)] || "steady";
  return (
    <div className="trends">
      <div className="section-head" style={{ marginTop: 0 }}>How the months have gone</div>
      <div className="trends__bars" role="img"
        aria-label={t.months.map((m) => `${m.full}: ${m.entries} ${m.entries === 1 ? "entry" : "entries"}`).join(". ")}>
        {t.months.map((m) => (
          <div key={m.sortKey} className="trends__col" title={`${m.full} — ${m.entries} ${m.entries === 1 ? "entry" : "entries"}`}>
            <div className={`trends__bar${m.entries === 0 ? " trends__bar--empty" : ""}`}
              style={{ height: `${Math.max(2, Math.round((m.entries / peak) * 76))}px` }} />
            <span className="trends__lbl">{m.label}</span>
          </div>
        ))}
      </div>

      <div className="trends__facts">
        <div className="trends__fact">
          <span className="trends__fig">{t.totalEntries}</span>
          <span className="trends__say">
            {t.totalEntries === 1 ? "entry" : "entries"}, across {t.totalDays} {t.totalDays === 1 ? "day" : "days"}
          </span>
        </div>
        <div className="trends__fact">
          <span className="trends__fig">{t.meanMood.toFixed(1)}</span>
          <span className="trends__say">average mood — {moodWord}, on your own scale</span>
        </div>
        {t.busiestWeekday && (
          <div className="trends__fact">
            <span className="trends__fig">{t.busiestWeekday}</span>
            <span className="trends__say">is when you write most</span>
          </div>
        )}
        {t.moodDelta !== null && Math.abs(t.moodDelta) >= 0.2 && (
          <div className="trends__fact">
            <span className="trends__fig">{t.moodDelta > 0 ? "+" : ""}{t.moodDelta.toFixed(1)}</span>
            <span className="trends__say">
              mood this past month against the one before
            </span>
          </div>
        )}
      </div>
      <p className="trends__note">Worked out on this device from your own entries — no AI, nothing sent.</p>
    </div>
  );
}

function ReadableExport({ entries }: { entries: JournalEntry[] }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const save = async (format: ReadableFormat) => {
    setBusy(true); setMsg(null);
    try {
      const text = buildReadable(entries, format);
      const ok = await saveReadableCopy(text, readableFilename(format), !isTauri());
      setMsg(ok ? "Saved — that copy is yours to keep." : null);
    } catch {
      setMsg("Could not save that copy.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="row" style={{ gap: "0.4rem", alignItems: "center" }}>
      <Download size={12} aria-hidden style={{ color: "var(--ink-3)" }} />
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--ink-2)" }}>Take a copy as</span>
      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void save("markdown")}
        title="Save your journal as Markdown you can read, print or keep">Markdown</button>
      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void save("text")}
        title="Save your journal as plain text you can read, print or keep">plain text</button>
      {msg && <span className="keeping__note" style={{ marginTop: 0 }}>{msg}</span>}
    </div>
  );
}

function OnThisDay({ entries, onOpenPhoto }: { entries: JournalEntry[]; onOpenPhoto: (id: string) => void }) {
  const items = useMemo(() => {
    const now = new Date();
    return entries
      .filter((e) => {
        const t = new Date(e.created_at);
        return t.getDate() === now.getDate() && !(t.getMonth() === now.getMonth() && t.getFullYear() === now.getFullYear());
      })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 3);
  }, [entries]);
  if (items.length === 0) return null;
  const ago = (iso: string) => {
    const t = new Date(iso);
    const now = new Date();
    const months = (now.getFullYear() - t.getFullYear()) * 12 + (now.getMonth() - t.getMonth());
    if (months >= 12) { const y = Math.floor(months / 12); return `${y} year${y === 1 ? "" : "s"} ago`; }
    return `${months} month${months === 1 ? "" : "s"} ago`;
  };
  // R4 — the flashback is a kept artifact, not a list row.
  return (
    <div className="flashback">
      <div className="flashback__head">
        <img src={onThisDayMark} alt="" className="flashback__mark" /> On this day
      </div>
      {items.map((e) => (
        <div key={e.id} className="flashback__item">
          <MoodMark mood={e.mood} size={14} title={`Mood ${e.mood} of 5 — ${MOOD_LABELS[e.mood - 1]}`} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="flashback__ago">{ago(e.created_at)} · {fmtDate(e.created_at)}</div>
            <div className="flashback__title">{e.title}</div>
            <div className="flashback__body" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", whiteSpace: "pre-wrap" }}>{e.body}</div>
          </div>
          {e.photo_ref?.startsWith("data:") && (
            <button type="button" aria-label="Open photo" onClick={() => onOpenPhoto(e.id)}
              style={{ padding: 0, border: "none", background: "none", cursor: "zoom-in", flexShrink: 0 }}>
              <img src={e.photo_ref} alt="" className="flashback__thumb" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// computeStreak RETIRED (R5) — no streak mechanics in the Nocturne restructure.

// R1 — mood as SIGNING the page: five moon-phase glyphs, no numbered chips.
function MoodPicker({ value, onChange }: { value: number; onChange: (m: number) => void }) {
  return (
    <div className="mood-sign">
      {[1, 2, 3, 4, 5].map((m) => (
        <button type="button" key={m} className={`mood-sign__glyph g${m} ${value === m ? "on" : ""}`}
          title={MOOD_LABELS[m - 1]}
          aria-label={`Mood ${m} of 5 — ${MOOD_LABELS[m - 1]}`} aria-pressed={value === m}
          style={{ backgroundImage: `url("${moodMarkUrl}")`, ...(value === m ? { boxShadow: "0 0 0 2px var(--elev-1), 0 0 0 3px var(--accent)" } : {}) }}
          onClick={() => onChange(m)} />
      ))}
      <span className="mood-sign__label">{MOOD_LABELS[value - 1]}</span>
    </div>
  );
}

function Compose({ editing, onDone, onCancel }: { editing: JournalEntry | null; onDone: () => void; onCancel: () => void }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mood, setMood] = useState(3);
  const [photoRef, setPhotoRef] = useState("");
  const [tags, setTags] = useState("");
  // The day's texture — what Day One and Journey both sell. Fetched on the DEVICE
  // (Open-Meteo, free, no key) and only when the writer asks for it; coordinates are
  // rounded to ~1km and never sent to Oriora.
  const [place, setPlace] = useState("");
  const [gettingPlace, setGettingPlace] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const prompt = useMemo(() => promptForToday(), []);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) { setTitle(editing.title); setBody(editing.body); setMood(editing.mood); setPhotoRef(editing.photo_ref?.startsWith("data:") ? editing.photo_ref : ""); setTags(editing.tags ?? ""); setPlace(editing.place ?? ""); }
  }, [editing]);

  function reset() { setTitle(""); setBody(""); setMood(3); setPhotoRef(""); setTags(""); setPlace(""); setErr(null); }

  /**
   * "Where I was" — the day's texture, on demand only.
   *
   * Coordinates are read by the OS, rounded to ~1km, and used ONLY to ask Open-Meteo (free, no
   * key) for today's weather. Nothing reaches Oriora, and nothing happens unless the writer taps.
   * Failure is silent-ish: the entry saves fine without it.
   */
  async function addPlace() {
    setGettingPlace(true);
    try {
      const pos = await new Promise<GeolocationPosition>((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 8000, maximumAge: 600000 }));
      const lat = Math.round(pos.coords.latitude * 100) / 100;
      const lon = Math.round(pos.coords.longitude * 100) / 100;
      const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code`);
      const j = await r.json();
      const t = Math.round(j?.current?.temperature_2m ?? NaN);
      const code = Number(j?.current?.weather_code ?? -1);
      const word =
        code === 0 ? "clear" : code <= 3 ? "cloudy" : code <= 48 ? "foggy"
        : code <= 67 ? "rain" : code <= 77 ? "snow" : code <= 82 ? "showers" : "storm";
      // Keep the rounded coordinates WITH the weather: the entry remembers where it was
      // written, on the device, so a map of your days stays possible later.
      setPlace(`${lat},${lon},${Number.isFinite(t) ? `${word} ${t}°` : word}`);
    } catch {
      setErr("Couldn't read the weather just now.");
    } finally { setGettingPlace(false); }
  }

  // Auto-title by date (matches the iOS app's auto-title behavior) when left blank.
  function autoTitle(): string {
    return new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  }

  // Optional photo per entry — ONE hidden <input type=file> on EVERY surface: iOS raises the
  // native photo/camera sheet, macOS the file picker, web the browser picker. The image is
  // ALWAYS stored as a downscaled JPEG data-URL in photo_ref, so it renders under the app CSP
  // (img-src 'self' data:), survives a cloud-backup restore on ANOTHER device, and opens in the
  // full-screen viewer. (The old desktop branch stored a raw file PATH — which the webview could
  // not render and which broke cross-device restore — and the web branch silently did nothing.)
  // The photo is filed on the device; the AI is only ever sent entry text/metadata, never the photo.
  function pickPhoto() {
    setErr(null);
    photoInputRef.current?.click();
  }
  function onPhotoInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking the same photo refires
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (typeof src !== "string") { setErr("Could not read that photo — try another."); return; }
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { setPhotoRef(src); return; }
        ctx.drawImage(img, 0, 0, w, h);
        try { setPhotoRef(canvas.toDataURL("image/jpeg", 0.82)); } catch { setPhotoRef(src); }
      };
      img.onerror = () => setPhotoRef(src);
      img.src = src;
    };
    reader.onerror = () => setErr("Could not read that photo — try another.");
    reader.readAsDataURL(file);
  }

  async function save(ev: React.FormEvent) {
    ev.preventDefault();
    const trimmedBody = body.trim();
    if (!trimmedBody) { setErr("Write something first."); return; }
    setBusy(true); setErr(null);
    try {
      const t = title.trim() || autoTitle();
      // The day's texture rides along as a tag, so it searches and filters like any other.
      if (editing) await store.updateEntry(editing.id, { title: t, body: trimmedBody, mood, photo_ref: photoRef, tags, place });
      else await store.createEntry({ title: t, body: trimmedBody, mood, photo_ref: photoRef, tags, place });
      reset(); onDone();
    } catch (e) { setErr(e instanceof Error ? e.message : "Could not save"); } finally { setBusy(false); }
  }

  // R1 — Today's Page: a page, not a widget. Date as the heading, the prompt whispered,
  // the fields open paper, mood as the signing gesture.
  return (
    <div className="today-page">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
        <div className="today-page__eyebrow">{editing ? "Edit entry" : "Today"}</div>
        {editing && <button type="button" className="btn-ghost" style={{ fontSize: "var(--fs-xs)", color: "var(--accent)", fontWeight: 600 }} onClick={() => { reset(); onCancel(); }}>Cancel edit</button>}
      </div>
      <h2 className="today-page__date">{autoTitle()}</h2>
      {!editing && (
        <div className="today-page__prompt">
          <img src={promptQuill} alt="" className="today-page__prompt-mark" />
          {prompt}
        </div>
      )}

      <form onSubmit={save} style={editing ? { marginTop: "var(--space-4)" } : undefined}>
        <input className="today-page__title" placeholder={`Title (optional — defaults to "${autoTitle()}")`} value={title} onChange={(e) => setTitle(e.target.value)} />
        <textarea className="today-page__field" rows={5} placeholder="Your reflection…" value={body} onChange={(e) => setBody(e.target.value)} />

        {/* Optional photo — filed on the device; the AI is never sent the photo. Quiet ghost attach. */}
        <div className="row" style={{ gap: "0.6rem", alignItems: "center", marginBottom: "0.35rem", flexWrap: "wrap" }}>
          {photoRef && (
            <button type="button" aria-label="View photo full screen" onClick={() => setShowPhoto(true)}
              style={{ padding: 0, border: "none", background: "none", cursor: "zoom-in" }}>
              <img src={photoRef} alt="" className="composer-attach__thumb" />
            </button>
          )}
          <button type="button" className="composer-attach" onClick={pickPhoto}>{photoRef ? "Change photo" : "Add photo"}</button>
          {photoRef && <button type="button" className="composer-attach" style={{ color: "var(--ink-3)" }} onClick={() => setPhotoRef("")}>Remove</button>}
          <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoInputChange} style={{ display: "none" }} />
          {place ? (
            <span className="chip" title="Where and what the weather was — saved with the entry on this device, and carried with it in a backup">
              {placeWeather(place) || "saved"}
              <button type="button" aria-label="Remove the weather" onClick={() => setPlace("")}
                style={{ border: "none", background: "none", padding: 0, color: "var(--ink-3)", display: "inline-flex" }}><X size={11} /></button>
            </span>
          ) : (
            <button type="button" className="composer-attach" disabled={gettingPlace} onClick={() => void addPlace()}
              title="Add today's weather — your rounded coordinates go to the open-meteo.com weather service to read it; Oriora is not involved in the reading">
              {gettingPlace ? "Reading…" : "Add weather"}
            </button>
          )}
        </div>
        <input className="composer-tags" value={tags} onChange={(ev) => setTags(ev.target.value)}
          placeholder="Tags, comma separated — work, family, running…" aria-label="Tags for this entry" />
        {showPhoto && photoRef && (
          <PhotoViewer photos={[{ src: photoRef, alt: "Photo attached to this entry" }]} onClose={() => setShowPhoto(false)} />
        )}

        <div className="today-page__foot">
          <div>
            <div className="mood-sign__eyebrow" style={{ marginBottom: 6 }}>Sign with your mood</div>
            <MoodPicker value={mood} onChange={setMood} />
          </div>
          <div className="row" style={{ gap: "0.6rem" }}>
            {err && <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err}</span>}
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Saving…" : editing ? "Update" : "Save entry"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

type ReflectError =
  | { kind: "insufficient_funds" }
  | { kind: "managed_ai_blocked" }
  | { kind: "unauthenticated" }
  | { kind: "offline" }
  | { kind: "empty" }
  | { kind: "unavailable"; message: string };

function WeeklyReflection({ entries, offline }: { entries: JournalEntry[]; offline: boolean }) {
  const [result, setResult] = useState<Reflection | null>(null);
  const [signals, setSignals] = useState<WeekSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ReflectError | null>(null);

  // Entries in the reflection window (last 28 days) — what the signals actually cover, so
  // the button isn't blocked when the last 7 days are empty but the prior weeks aren't.
  const windowCount = useMemo(() => {
    const since = Date.now() - 28 * 86_400_000;
    return entries.filter((e) => new Date(e.created_at).getTime() >= since).length;
  }, [entries]);
  // Entries specifically in the last 7 days — for the "this week vs baseline" copy.
  const weekCount = useMemo(() => {
    const since = Date.now() - 7 * 86_400_000;
    return entries.filter((e) => new Date(e.created_at).getTime() >= since).length;
  }, [entries]);

  async function run() {
    if (offline) { setErr({ kind: "offline" }); return; }
    setBusy(true); setErr(null); setResult(null); setSignals(null);
    try {
      // week_signals is computed in Rust from METADATA ONLY (mood/title/timestamp + body
      // CHAR-length) — no body text leaves the device. This richer aggregate is what the
      // brain reflects on, so it can name specific, non-obvious patterns.
      const sig = await store.weekSignals();
      const r = await weeklyReflection(sig);
      if (!r.text) { setErr({ kind: "empty" }); return; }
      setResult(r);
      setSignals(sig); // keep the computed signals so the UI can show what was reflected on
    } catch (e) {
      if (e instanceof BrainError) {
        if (e.code === "insufficient_funds") setErr({ kind: "insufficient_funds" });
        else if (e.code === "managed_ai_blocked") setErr({ kind: "managed_ai_blocked" });
        else if (e.code === "unauthenticated") setErr({ kind: "unauthenticated" });
        else setErr({ kind: "unavailable", message: e.message });
      } else {
        setErr({ kind: "unavailable", message: "Reflection failed — please try again." });
      }
    } finally {
      setBusy(false);
    }
  }

  const disabled = busy || windowCount === 0 || offline;

  // R2 — a companion offering an observation, not a doorman; honesty line verbatim.
  return (
    <div className="card reflection-card">
      <div className="reflection-card__head">
        <div className="reflection-card__title">
          <img src={reflectionMark} alt="" className="reflection-card__mark" /> Weekly reflection
        </div>
        <button className="btn btn-primary row" style={{ fontSize: "var(--fs-xs)", gap: "0.35rem", flexShrink: 0 }} disabled={disabled} onClick={run}>
          {busy
            ? <><Sparkles size={13} className="spin-slow" /> Reflecting…</>
            : result
              ? <><RefreshCw size={13} /> Reflect again</>
              : "Reflect on my week"}
        </button>
      </div>

      <div className="reflection-card__note">
        {windowCount === 0
          ? "Write a few entries, then come back to reflect on the pattern."
          : <>Reads your last 4 weeks ({windowCount} entr{windowCount !== 1 ? "ies" : "y"}, {weekCount} in the last 7 days) to set this week against your own baseline. This reflection sends only titles, moods, and dates — <b>not your text</b>.</>}
      </div>

      {busy && (
        <div style={{ marginTop: "0.85rem" }}>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "60%" }} />
        </div>
      )}

      {!busy && err && <ReflectErrorView err={err} />}

      {!busy && !err && result && (
        <div style={{ marginTop: "0.85rem" }}>
          <p className="reflection-card__result" style={{ margin: 0 }}>{result.text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {signals && <FactList s={signals} />}
          {signals && <SignalsStrip s={signals} />}
          <div className="row" style={{ marginTop: "0.7rem" }}>
            <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.3rem" }}>
              <Sparkles size={11} /> An observation, not advice.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// The receipts: the pre-ranked, ready-to-say observational facts the reflection was grounded
// in — computed on-device in Rust (db.rs). Shows the user the exact numbers the AI saw, so the
// observation reads as derived from real data, not vibes. Purely observational; no body text.
function FactList({ s }: { s: WeekSignals }) {
  const facts = (s.headline_facts ?? []).slice(0, 5);
  if (facts.length === 0) return null;
  const dotClass = (t: FactTone) => (t === "up" ? "up" : t === "down" ? "down" : "");
  return (
    <div style={{ marginTop: "0.85rem", paddingTop: "0.55rem", borderTop: "1px solid var(--border)" }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 600, marginBottom: "0.2rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        The numbers behind this
      </div>
      {facts.map((f, i) => (
        <div key={i} className="fact">
          <span className={`fact-dot ${dotClass(f.tone)}`} />
          <span className="fact-text tnum">{f.text}</span>
        </div>
      ))}
    </div>
  );
}

// Shows the exact on-device computed signals the reflection was grounded in — so the
// observation reads as derived from real numbers, not vibes. Pure metadata aggregates;
// no entry body is ever shown or sent. Backward-tolerant: skips any missing field.
function SignalsStrip({ s }: { s: WeekSignals }) {
  const chips: { label: string; value: string }[] = [];
  if (s.mood && Number.isFinite(s.mood.mean)) {
    chips.push({ label: "Avg mood", value: `${s.mood.mean.toFixed(1)} / 5` });
  }
  if (s.mood && Number.isFinite(s.mood.variance)) {
    const swing = s.mood.variance < 0.5 ? "steady" : s.mood.variance < 1.5 ? "some swing" : "high swing";
    chips.push({ label: "Variability", value: `${s.mood.variance.toFixed(2)} · ${swing}` });
  }
  const peak = (s.by_weekday ?? []).reduce<{ weekday: string; mean_mood: number } | null>(
    (best, d) => (best === null || d.mean_mood > best.mean_mood ? d : best),
    null,
  );
  if (peak) chips.push({ label: "Highest day", value: `${peak.weekday} (${peak.mean_mood.toFixed(1)})` });
  if (s.cadence) {
    chips.push({ label: "Cadence", value: `${s.active_days}/${s.window_days} days · ${s.cadence.gap_days} gap${s.cadence.gap_days === 1 ? "" : "s"}` });
    if (s.cadence.current_streak > 0) chips.push({ label: "Streak", value: `${s.cadence.current_streak} day${s.cadence.current_streak === 1 ? "" : "s"}` });
  }
  const kw = (s.title_keywords ?? []).slice(0, 3);
  if (kw.length) chips.push({ label: "Top titles", value: kw.map((k) => `${k.word}×${k.count}`).join(", ") });
  if (s.length && s.length.mean_chars > 0) chips.push({ label: "Avg length", value: `${Math.round(s.length.mean_chars)} chars` });

  if (chips.length === 0) return null;
  return (
    <div className="signals-strip" style={{ marginTop: "0.7rem", display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
      {chips.map((c) => (
        <span key={c.label} className="signal-chip" title={`Computed on this device from your entry metadata — ${c.label.toLowerCase()}`}
          style={{ display: "inline-flex", alignItems: "baseline", gap: "0.3rem", fontSize: "var(--fs-xs)", padding: "0.2rem 0.55rem", borderRadius: 999, background: "var(--accent-soft)", color: "var(--ink-2)", lineHeight: 1.4 }}>
          <span className="muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em" }}>{c.label}</span>
          <b className="tnum" style={{ fontWeight: 600, color: "var(--ink-1)" }}>{c.value}</b>
        </span>
      ))}
    </div>
  );
}

function ReflectErrorView({ err }: { err: ReflectError }) {
  if (err.kind === "insufficient_funds") {
    return (
      <div className="reflect-error">
        <div className="row" style={{ gap: "0.4rem", fontWeight: 600, color: "var(--ink-1)" }}>
          <AlertCircle size={14} style={{ color: "var(--amber)" }} /> Add funds to your Oriora account
        </div>
        <p className="muted" style={{ margin: "0.35rem 0 0.6rem", fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>
          {platform() === "ios"
            ? "Open the account menu and tap Add funds to top up, then try the weekly reflection again. Your writing keeps saving here."
            : "Add funds in your Oriora account on the website to keep using the weekly reflection. Your writing keeps saving here."}
        </p>
        <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)" }}
          onClick={() => void openWebLogin()}>
          Open Oriora
        </button>
      </div>
    );
  }
  if (err.kind === "managed_ai_blocked") {
    return (
      <div className="reflect-error">
        <div className="row" style={{ gap: "0.4rem", fontWeight: 600, color: "var(--ink-1)" }}>
          <AlertCircle size={14} style={{ color: "var(--amber)" }} /> AI needs a vendor key
        </div>
        <p className="muted" style={{ margin: "0.35rem 0 0", fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>
          Connect a vendor key in your Oriora settings to enable AI for this app.
        </p>
      </div>
    );
  }
  if (err.kind === "unauthenticated") {
    return (
      <div className="reflect-error">
        <div className="row" style={{ gap: "0.4rem", fontWeight: 600, color: "var(--ink-1)" }}>
          <AlertCircle size={14} style={{ color: "var(--amber)" }} /> Your session expired
        </div>
        <p className="muted" style={{ margin: "0.35rem 0 0.6rem", fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>
          Sign in to Oriora again to reflect on your week.
        </p>
        <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => supabase.auth.signOut()}>Sign in again</button>
      </div>
    );
  }
  if (err.kind === "offline") {
    return (
      <div className="reflect-error">
        <div className="row" style={{ gap: "0.4rem", fontWeight: 600, color: "var(--ink-1)" }}>
          <WifiOff size={14} style={{ color: "var(--amber)" }} /> You're offline
        </div>
        <p className="muted" style={{ margin: "0.35rem 0 0", fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>
          Reconnect to run the reflection. Your entries are safe on this device in the meantime.
        </p>
      </div>
    );
  }
  if (err.kind === "empty") {
    return (
      <div className="reflect-error">
        <div className="row" style={{ gap: "0.4rem", fontWeight: 600, color: "var(--ink-1)" }}>
          <AlertCircle size={14} style={{ color: "var(--ink-3)" }} /> Nothing came back
        </div>
        <p className="muted" style={{ margin: "0.35rem 0 0", fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>
          The reflection came back empty. Add another entry or two this week and try again.
        </p>
      </div>
    );
  }
  return (
    <div className="reflect-error">
      <div className="row" style={{ gap: "0.4rem", fontWeight: 600, color: "var(--ink-1)" }}>
        <AlertCircle size={14} style={{ color: "var(--rose)" }} /> {err.message}
      </div>
      <p className="muted" style={{ margin: "0.35rem 0 0", fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>
        Press “Reflect on my week” to try again.
      </p>
    </div>
  );
}

function EntryRow({ e, onEdit, onChange, onOpenPhoto }: { e: JournalEntry; onEdit: () => void; onChange: () => void; onOpenPhoto: () => void }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  async function remove() { setConfirmDelete(false); setBusy(true); await store.deleteEntry(e.id); onChange(); }
  const d = new Date(e.created_at);
  const day = d.getDate();
  const weekday = d.toLocaleDateString("en-US", { weekday: "short" });
  // R3 — the moon-mark replaces the emoji face; same labels via tooltip.
  return (
    <div className="entry-row">
      {/* date rail — the journaling domain leads with a big day-number (My Diary / Day One) */}
      <div className="entry-row__date" title={`${fmtDate(e.created_at)} · ${fmtTime(e.created_at)}`}>
        <div className="entry-row__day tnum">{day}</div>
        <div className="entry-row__weekday">{weekday}</div>
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem", alignItems: "flex-start", flexWrap: "nowrap" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1rem", lineHeight: 1.25, minWidth: 0 }}>{e.title}</div>
          <MoodMark mood={e.mood} size={16} title={`Mood ${e.mood} of 5 — ${MOOD_LABELS[e.mood - 1]}`} />
        </div>
        <div style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", marginTop: 4, lineHeight: 1.55, whiteSpace: "pre-wrap", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{e.body}</div>
        {(tagList(e.tags ?? "").length > 0 || placeWeather(e.place ?? "")) && (
          <div className="entry-tags">
            {placeWeather(e.place ?? "") && (
              <span className="entry-tag" title="The weather where you wrote this">{placeWeather(e.place)}</span>
            )}
            {tagList(e.tags ?? "").map((t) => <span key={t} className="entry-tag">{t}</span>)}
          </div>
        )}
        {/* G21 — the photo OPENS: tap → full screen with pinch/zoom/rotate (PhotoViewer). */}
        {e.photo_ref?.startsWith("data:") && (
          <button type="button" aria-label="Open photo full screen" onClick={onOpenPhoto}
            style={{ padding: 0, border: "none", background: "none", cursor: "zoom-in", display: "block" }}>
            <img src={e.photo_ref} alt="" style={{ marginTop: 8, maxHeight: 150, maxWidth: "100%", objectFit: "cover", borderRadius: 10 }} />
          </button>
        )}
        <div className="row entry-actions" style={{ gap: "0.2rem", marginTop: 6 }}>
          <button className="btn" style={{ padding: "0.2rem 0.35rem", display: "inline-flex", alignItems: "center", color: "var(--ink-3)" }} title="Edit" aria-label="Edit entry" onClick={onEdit}><Pencil size={13} /></button>
          <button disabled={busy} className="btn" style={{ padding: "0.2rem 0.35rem", color: "var(--ink-3)", display: "inline-flex", alignItems: "center" }} title="Delete" aria-label="Delete entry" onClick={() => setConfirmDelete(true)}><Trash2 size={13} /></button>
        </div>
      </div>
      {confirmDelete && (
        <div role="dialog" aria-modal="true" aria-label="Delete entry"
          onClick={() => setConfirmDelete(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(26,22,38,0.35)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
          <div className="card" onClick={(ev) => ev.stopPropagation()} style={{ width: "100%", maxWidth: 340, padding: "1.25rem", boxShadow: "var(--shadow-2)" }}>
            <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.5rem" }}>Delete entry?</div>
            <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem" }}>
              Delete <b>{e.title}</b>? This can't be undone.
            </p>
            <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
              <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => setConfirmDelete(false)}><X size={13} /> Cancel</button>
              <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", background: "var(--rose)", borderColor: "var(--rose)" }} onClick={remove}><Check size={13} /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
